// TODO: brancher Stripe en multi-currency. Pour l'instant Stripe = EUR.
// Le backend devra créer des prix Stripe en USD avant activation.
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, Check, Shield, Lock, CreditCard, Users, X } from 'lucide-react';
import { ALL_TESTS, DISCOVERY_AVAILABLE_TESTS } from '../data/testsData';
import LanguageSelector from '../components/LanguageSelector';
import CurrencySelector from '../components/CurrencySelector';
import useTranslation from '../data/useTranslation';
import { useCurrency } from '../contexts/CurrencyContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Checkout() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const packId = searchParams.get('pack') || 'complete';
  const { t } = useTranslation();
  const { format, currency } = useCurrency();
  
  const [prenom, setPrenom] = useState('');
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [prenom2, setPrenom2] = useState('');
  const [nom2, setNom2] = useState('');
  const [email2, setEmail2] = useState('');
  const [selectedTests, setSelectedTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [waivedWithdrawal, setWaivedWithdrawal] = useState(false);

  const submittedRef = useRef(false);
  const emailRef = useRef('');

  useEffect(() => { emailRef.current = email; }, [email]);

  useEffect(() => {
    try {
      if (window.posthog && typeof window.posthog.capture === 'function') {
        window.posthog.capture('checkout_started', { pack: packId, currency });
      }
    } catch (e) { /* ignore */ }
    return () => {
      if (!submittedRef.current) {
        try {
          if (window.posthog && typeof window.posthog.capture === 'function') {
            window.posthog.capture('checkout_abandoned', { pack: packId, has_email: !!emailRef.current });
          }
        } catch (e) { /* ignore */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packId]);

  const PACK_INFO = {
    decouverte: {
      name: t.landing.discoveryName,
      price: 9.99,
      testsAllowed: 2,
      features: t.checkout.discoveryFeatures,
      testsToShow: DISCOVERY_AVAILABLE_TESTS
    },
    complete: {
      name: t.landing.completeName,
      price: 29.99,
      testsAllowed: 6,
      features: t.checkout.completeFeatures,
      testsToShow: ALL_TESTS
    },
    duo: {
      name: t.landing.duoName,
      price: 39.99,
      testsAllowed: 6,
      features: t.checkout.duoFeatures,
      testsToShow: ALL_TESTS
    }
  };

  const pack = PACK_INFO[packId] || PACK_INFO.complete;
  const isDuo = packId === 'duo';
  const isDiscovery = packId === 'decouverte';

  const handleTestSelect = (testId) => {
    if (!isDiscovery) return;
    if (selectedTests.includes(testId)) {
      setSelectedTests(selectedTests.filter(tid => tid !== testId));
    } else if (selectedTests.length < 2) {
      setSelectedTests([...selectedTests, testId]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!prenom.trim() || !nom.trim()) { setError(t.checkout.errorFirstName); return; }
    if (!email || !email.includes('@')) { setError(t.checkout.errorEmail); return; }
    if (isDuo && (!prenom2.trim() || !nom2.trim())) { setError(t.checkout.errorDuoName); return; }
    if (isDuo && (!email2 || !email2.includes('@'))) { setError(t.checkout.errorDuoEmail); return; }
    if (isDiscovery && selectedTests.length !== 2) { setError(t.checkout.errorTests); return; }
    if (!acceptedTerms || !waivedWithdrawal) { setError(t.checkout.mustAcceptAll); return; }

    const nowIso = new Date().toISOString();

    setLoading(true);
    submittedRef.current = true;
    try {
      const response = await fetch(`${API_URL}/api/checkout/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pack: packId,
          prenom: prenom.trim(),
          nom: nom.trim(),
          email: email.toLowerCase(),
          prenom2: isDuo ? prenom2.trim() : null,
          nom2: isDuo ? nom2.trim() : null,
          email2: isDuo ? email2.toLowerCase() : null,
          selected_tests: isDiscovery ? selectedTests : null,
          currency: currency || 'EUR',
          accepted_terms_at: nowIso,
          waived_withdrawal_at: nowIso
        })
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else if (data.session_id) {
        const simulateResponse = await fetch(`${API_URL}/api/checkout/simulate-payment?session_id=${data.session_id}`, { method: 'POST' });
        if (simulateResponse.ok) navigate(`/merci?session_id=${data.session_id}`);
      }
    } catch (err) {
      setError(t.checkout.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#111827] text-white">
      <header className="border-b border-gray-800 bg-[#111827]/95 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors" data-testid="checkout-back">
            <ArrowLeft className="w-5 h-5" />
            <span>{t.checkout.returnBtn}</span>
          </button>
          <div className="flex items-center gap-4">
            <CurrencySelector className="hidden sm:flex" />
            <LanguageSelector />
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-green-500" />
              <span className="text-sm text-gray-400">{t.checkout.securePayment}</span>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10">
            {/* Order Summary */}
            <div>
              <h1 className="text-3xl font-bold mb-6" data-testid="checkout-title">{t.checkout.orderTitle}</h1>
              
              <div className="p-6 rounded-2xl bg-[#1F2937] border border-gray-700 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-bold text-white">{pack.name}</h2>
                    <p className="text-gray-400 text-sm">{t.checkout.brandName}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-orange-500" data-testid="checkout-pack-price">{format(pack.price)}</p>
                    {isDuo && <p className="text-sm text-gray-500">{t.checkout.forTwoPersons}</p>}
                  </div>
                </div>
                <div className="border-t border-gray-700 pt-4">
                  <ul className="space-y-2">
                    {pack.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-gray-300 text-sm">
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {isDiscovery && (
                <div className="p-6 rounded-2xl bg-[#1F2937] border border-gray-700">
                  <h3 className="font-bold text-white mb-4">{t.checkout.chooseTests}</h3>
                  <p className="text-gray-400 text-sm mb-4">{t.checkout.chooseTestsSub}</p>
                  <div className="space-y-3">
                    {DISCOVERY_AVAILABLE_TESTS.map((test) => (
                      <button key={test.id} onClick={() => handleTestSelect(test.id)}
                        className={`w-full p-4 rounded-xl border transition-all flex items-center gap-4 text-left ${
                          selectedTests.includes(test.id) ? 'border-orange-500 bg-orange-500/10' : 'border-gray-700 hover:border-gray-600'
                        }`} data-testid={`test-select-${test.id}`}>
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          selectedTests.includes(test.id) ? 'border-orange-500 bg-orange-500' : 'border-gray-600'
                        }`}>
                          {selectedTests.includes(test.id) && <Check className="w-4 h-4 text-white" />}
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-white">{test.name}</p>
                          <p className="text-sm text-gray-400">{test.description}</p>
                        </div>
                        <span className="text-xs text-gray-500">{test.duration}</span>
                      </button>
                    ))}
                  </div>
                  <p className="text-center text-sm text-gray-500 mt-4">{selectedTests.length}/2 {t.checkout.testsSelected}</p>
                  <div className="mt-4 p-3 rounded-lg bg-gray-800/50 border border-gray-700">
                    <p className="text-xs text-gray-400 flex items-center gap-2">
                      <X className="w-4 h-4 text-gray-500" />
                      {t.checkout.mbtiNote}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-4 mt-6 text-sm text-gray-500">
                <div className="flex items-center gap-1.5">
                  <Shield className="w-4 h-4 text-green-500" />
                  <span>{t.checkout.securePayment}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CreditCard className="w-4 h-4 text-blue-500" />
                  <span>{t.checkout.cbVisa}</span>
                </div>
              </div>
            </div>

            {/* Form */}
            <div>
              <h2 className="text-xl font-bold mb-6">{t.checkout.yourInfo}</h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {isDuo && <p className="text-orange-400 font-medium text-sm">{t.checkout.person1}</p>}
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">{t.checkout.firstName} *</label>
                    <input type="text" value={prenom} onChange={(e) => setPrenom(e.target.value)}
                      placeholder={t.checkout.firstName}
                      className="w-full px-4 py-3 bg-[#1F2937] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none transition-colors"
                      required data-testid="checkout-prenom" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">{t.checkout.lastName} *</label>
                    <input type="text" value={nom} onChange={(e) => setNom(e.target.value)}
                      placeholder={t.checkout.lastName}
                      className="w-full px-4 py-3 bg-[#1F2937] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none transition-colors"
                      required data-testid="checkout-nom" />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">{t.checkout.email} *</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder={t.checkout.emailPlaceholder}
                    className="w-full px-4 py-3 bg-[#1F2937] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none transition-colors"
                    required data-testid="checkout-email" />
                </div>

                {isDuo && (
                  <>
                    <div className="border-t border-gray-700 pt-4 mt-4">
                      <p className="text-orange-400 font-medium text-sm mb-4">{t.checkout.person2}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">{t.checkout.firstName} *</label>
                        <input type="text" value={prenom2} onChange={(e) => setPrenom2(e.target.value)}
                          placeholder={t.checkout.firstName}
                          className="w-full px-4 py-3 bg-[#1F2937] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none transition-colors"
                          required data-testid="checkout-prenom2" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">{t.checkout.lastName} *</label>
                        <input type="text" value={nom2} onChange={(e) => setNom2(e.target.value)}
                          placeholder={t.checkout.lastName}
                          className="w-full px-4 py-3 bg-[#1F2937] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none transition-colors"
                          required data-testid="checkout-nom2" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">{t.checkout.email} *</label>
                      <input type="email" value={email2} onChange={(e) => setEmail2(e.target.value)}
                        placeholder={t.checkout.email2Placeholder}
                        className="w-full px-4 py-3 bg-[#1F2937] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none transition-colors"
                        required data-testid="checkout-email2" />
                    </div>
                    <p className="text-xs text-gray-500">
                      <Users className="w-3 h-3 inline mr-1" />
                      {t.checkout.duoNote}
                    </p>
                  </>
                )}

                {error && (
                  <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm" data-testid="checkout-error">{error}</div>
                )}

                {/* Legal consent checkboxes */}
                <div className="space-y-3 pt-2">
                  <label className="flex items-start gap-3 cursor-pointer group" data-testid="checkout-accept-terms-label">
                    <input
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-orange-500 cursor-pointer flex-shrink-0"
                      data-testid="checkout-accept-terms"
                      required
                    />
                    <span className="text-xs text-gray-400 leading-snug">
                      {t.checkout.acceptTermsCheck}{' '}
                      <Link to="/cgv" target="_blank" className="text-orange-400 hover:underline" data-testid="checkout-terms-link">{t.checkout.termsLink}</Link>
                      {' '}{t.checkout.acceptTermsCheckEnd}{' '}
                      <Link to="/privacy" target="_blank" className="text-orange-400 hover:underline" data-testid="checkout-privacy-link">{t.checkout.privacyLink}</Link>.
                    </span>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer group" data-testid="checkout-waive-withdrawal-label">
                    <input
                      type="checkbox"
                      checked={waivedWithdrawal}
                      onChange={(e) => setWaivedWithdrawal(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-orange-500 cursor-pointer flex-shrink-0"
                      data-testid="checkout-waive-withdrawal"
                      required
                    />
                    <span className="text-xs text-gray-400 leading-snug">{t.checkout.waiveWithdrawal}</span>
                  </label>
                </div>

                <button type="submit" disabled={loading || (isDiscovery && selectedTests.length !== 2) || !acceptedTerms || !waivedWithdrawal}
                  className="w-full py-4 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2"
                  data-testid="checkout-submit">
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      {t.checkout.processing}
                    </>
                  ) : (
                    <>
                      <Lock className="w-5 h-5" />
                      {t.checkout.payBtn} {format(pack.price)}
                    </>
                  )}
                </button>

                <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/30">
                  <p className="text-green-400 text-sm font-medium text-center">{t.checkout.guarantee}</p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Checkout;
